import {
  AgxButton,
  AgxDropdown,
  AgxSpacer,
  AgxTypography,
  Icon_Refresh,
  type DropdownOption,
  useToast,
} from '@urbanx/agx.ui-components2';
import {
  type AgencyAgentModel,
  useGetAllAgencies,
  useStaffGetAgentsInAgency,
} from '@urbanx/agx.agencies.client';
import {
  Provider,
  useGetAgentIntegrationStatus,
  useInvalidateUseGetAgentIntegrationStatus,
  useResetAgentIntegration,
} from '@urbanx/agx.support.client';
import LoadingState from 'components/loadingState/LoadingState';
import Panel from 'components/panel/Panel';
import { useState } from 'react';

import SupportBackButton from '../SupportBackButton';
import IntegrationCard from './components/IntegrationCard';

const getAgentName = (agent: AgencyAgentModel) =>
  agent.name?.fullName ||
  [agent.name?.firstName, agent.name?.lastName].filter((namePart) => !!namePart).join(' ') ||
  'Unnamed agent';

const AgentIntegrations = () => {
  const { pushToast } = useToast();
  const [selectedAgencyId, setSelectedAgencyId] = useState('');
  const [selectedAgentId, setSelectedAgentId] = useState('');

  const {
    data: agencies,
    isPending: agenciesPending,
    isError: agenciesError,
  } = useGetAllAgencies();
  const {
    data: agents,
    isPending: agentsPending,
    isError: agentsError,
  } = useStaffGetAgentsInAgency(selectedAgencyId, !!selectedAgencyId);
  const {
    data: integrationStatus,
    isPending: integrationStatusPending,
    isError: integrationStatusError,
  } = useGetAgentIntegrationStatus(
    selectedAgencyId,
    selectedAgentId,
    !!selectedAgencyId && !!selectedAgentId
  );
  const invalidateIntegrationStatus = useInvalidateUseGetAgentIntegrationStatus();
  const { resetAgentIntegration, resetAgentIntegrationPending } = useResetAgentIntegration({
    onSuccess: async (_returnData, inputData) => {
      await invalidateIntegrationStatus(inputData.agencyId, inputData.agentId);

      pushToast({ message: 'Integration connection reset', type: 'success' });
    },
    onError: () => {
      pushToast({ message: 'Unable to reset integration connection', type: 'error' });
    },
  });

  const agencyOptions: DropdownOption[] = (agencies ?? [])
    .filter((agency) => !!agency.id)
    .map((agency) => ({
      label: agency.name ?? 'Unnamed agency',
      value: agency.id!,
      searchText: agency.name ?? '',
    }));

  const activeAgents = (agents ?? []).filter((agent) => !!agent.id && !agent.softDeleted);
  const agentOptions: DropdownOption[] = activeAgents.map((agent) => ({
    label: `${getAgentName(agent)} (${agent.id})`,
    value: agent.id!,
    searchText: `${getAgentName(agent)} ${agent.id}`,
  }));

  const onAgencyChange = (agencyId: string) => {
    setSelectedAgencyId(agencyId);
    setSelectedAgentId('');
  };

  const resetIntegration = (provider: Provider) => {
    void resetAgentIntegration({
      agencyId: selectedAgencyId,
      agentId: selectedAgentId,
      provider,
    });
  };

  const selectionPending = agenciesPending || (!!selectedAgencyId && agentsPending);
  const showIntegrationCards =
    !!selectedAgentId && !!integrationStatus && !integrationStatusPending;

  return (
    <AgxSpacer direction="vertical" gap="spacing-xl" fill>
      <SupportBackButton />

      <Panel
        title="Agent Integrations"
        actionContent={
          <AgxButton
            id="agent-integrations-refresh"
            text="Refresh"
            variant="hollow"
            size="small"
            leftIcon={<Icon_Refresh width={16} height={16} />}
            disabled={!selectedAgentId || integrationStatusPending || resetAgentIntegrationPending}
            onClick={() => void invalidateIntegrationStatus(selectedAgencyId, selectedAgentId)}
          />
        }
      >
        <AgxSpacer direction="vertical" gap="spacing-xl" fill>
          <AgxSpacer direction="horizontal" gap="spacing-lg" contentAlignment="end" fill wrap>
            <AgxDropdown
              id="agent-integrations-agency"
              label="Agency"
              selected={selectedAgencyId}
              options={agencyOptions}
              onChange={onAgencyChange}
              placeholder="Select agency"
              searchable
              searchPlaceholder="Search agencies"
              stretch
              hideOptionalText
              disabled={agenciesPending || resetAgentIntegrationPending}
            />

            {!!selectedAgencyId && !agentsPending && (
              <AgxDropdown
                id="agent-integrations-agent"
                label="Agent"
                selected={selectedAgentId}
                options={agentOptions}
                onChange={setSelectedAgentId}
                placeholder="Select agent"
                searchable
                searchPlaceholder="Search agents"
                stretch
                hideOptionalText
                disabled={agentOptions.length === 0 || resetAgentIntegrationPending}
              />
            )}
          </AgxSpacer>

          {selectionPending && <LoadingState />}

          {(agenciesError || agentsError) && (
            <AgxTypography id="agent-integrations-selection-error" type="body" size="small">
              Unable to load agencies and agents.
            </AgxTypography>
          )}

          {!!selectedAgencyId && !agentsPending && !agentsError && agentOptions.length === 0 && (
            <AgxTypography id="agent-integrations-agents-empty" type="body" size="small">
              No active agents were found for this agency.
            </AgxTypography>
          )}
        </AgxSpacer>
      </Panel>

      {!!selectedAgentId && integrationStatusPending && <LoadingState />}

      {integrationStatusError && (
        <AgxTypography id="agent-integrations-status-error" type="body" size="small">
          Unable to load the selected agent's integration connections.
        </AgxTypography>
      )}

      {showIntegrationCards && (
        <AgxSpacer direction="horizontal" gap="spacing-xl" fill wrap>
          <IntegrationCard
            name="DocuSign"
            connected={integrationStatus.docuSignConnected === true}
            metadata={integrationStatus.docuSignMetadata}
            externalUrl={integrationStatus.docuSignAdminUrl}
            resetPending={resetAgentIntegrationPending}
            onReset={() => resetIntegration(Provider.DocuSign)}
          />
          <IntegrationCard
            name="RP Data"
            connected={integrationStatus.rpDataConnected === true}
            metadata={integrationStatus.rpDataMetadata}
            resetPending={resetAgentIntegrationPending}
            onReset={() => resetIntegration(Provider.RpData)}
          />
        </AgxSpacer>
      )}
    </AgxSpacer>
  );
};

export default AgentIntegrations;
